import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Loader2, Link as LinkIcon, FileText, Upload, Sparkles, MapPin, DollarSign, GraduationCap,
    ShieldCheck, Send, Copy, Trash2, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const MODES = [
    { key: "url", icon: LinkIcon, label: "URL", hint: "LinkedIn, Indeed, or a company career page" },
    { key: "text", icon: FileText, label: "Paste JD", hint: "Any raw job description text" },
    { key: "file", icon: Upload, label: "Upload", hint: "PDF, DOCX, or TXT (up to 8MB)" },
];

export default function RecruitJobParser() {
    const navigate = useNavigate();
    const [mode, setMode] = useState("url");
    const [url, setUrl] = useState("");
    const [text, setText] = useState("");
    const [file, setFile] = useState(null);
    const [phase, setPhase] = useState("idle"); // idle | parsing | ready | error
    const [progressStep, setProgressStep] = useState(0);
    const [brief, setBrief] = useState(null);
    const [history, setHistory] = useState([]);
    const [error, setError] = useState("");
    const fileRef = useRef(null);

    // Rotating progress copy so the ~6-12s LLM parse doesn't feel like
    // the button is broken. Total copy time exceeds any realistic parse
    // — the last message stays sticky until the request returns.
    const PARSE_STEPS = React.useMemo(() => [
        { at: 0,    label: "Reading the job description…" },
        { at: 2500, label: "Extracting titles, seniority, and salary…" },
        { at: 5000, label: "Distilling must-haves and red flags…" },
        { at: 8000, label: "Composing the AI Hiring Brief™…" },
        { at: 11000, label: "Almost done — polishing the output…" },
    ], []);

    useEffect(() => {
        if (phase !== "parsing") { setProgressStep(0); return; }
        const start = Date.now();
        const timers = PARSE_STEPS.map((s, i) => setTimeout(() => {
            // Only advance if we're still parsing (avoid a stale timer
            // firing after the phase flipped to ready/error).
            if (Date.now() - start >= s.at) setProgressStep(i);
        }, s.at));
        return () => timers.forEach(clearTimeout);
    }, [phase, PARSE_STEPS]);

    const loadHistory = () => api.get("/admin/recruit/searches?limit=10").then((r) => setHistory(r.data.items || []));
    useEffect(() => { loadHistory(); }, []);

    const parse = async () => {
        setError("");
        // Validate BEFORE we flip to the parsing phase — otherwise an early
        // toast-and-return leaves the Generate button permanently disabled.
        if (mode === "url" && !url.trim()) { toast.error("Paste a URL first."); return; }
        if (mode === "text" && text.trim().length < 50) { toast.error("Paste at least 50 characters of the JD."); return; }
        if (mode === "file" && !file) { toast.error("Choose a file first."); return; }
        try {
            setPhase("parsing");
            let res;
            if (mode === "url") {
                res = await api.post("/admin/recruit/job-parser/parse", { source_type: "url", url: url.trim(), save: true }, { timeout: 90_000 });
            } else if (mode === "text") {
                res = await api.post("/admin/recruit/job-parser/parse", { source_type: "text", text, save: true }, { timeout: 90_000 });
            } else {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("save", "true");
                res = await api.post("/admin/recruit/job-parser/parse-file", fd, {
                    headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000,
                });
            }
            setBrief(res.data);
            setPhase("ready");
            loadHistory();
            toast.success("AI Hiring Brief™ generated.");
        } catch (err) {
            const msg = formatApiError(err);
            setError(msg);
            setPhase("error");
            toast.error(msg);
        }
    };

    const reset = () => { setBrief(null); setPhase("idle"); setError(""); setUrl(""); setText(""); setFile(null); };

    return (
        <div data-testid="recruit-job-parser" className="space-y-6">
            {!brief && (
                <div className="card-soft p-5">
                    <div className="mb-4">
                        <h2 className="font-heading font-medium text-lg">New search</h2>
                        <p className="text-sm text-muted-foreground">Give us the job — we&apos;ll extract skills, salary, keywords, and generate an AI Hiring Brief™.</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                        {MODES.map((m) => (
                            <button
                                key={m.key}
                                onClick={() => setMode(m.key)}
                                data-testid={`parser-mode-${m.key}`}
                                className={`p-3 rounded-lg border text-left transition ${mode === m.key ? "border-violet-500 bg-violet-500/5" : "border-border hover:border-violet-500/30"}`}
                            >
                                <div className="flex items-center gap-2 font-medium text-sm">
                                    <m.icon size={14} className={mode === m.key ? "text-violet-600" : "text-muted-foreground"} /> {m.label}
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-1">{m.hint}</div>
                            </button>
                        ))}
                    </div>

                    {mode === "url" && (
                        <Input
                            placeholder="https://www.linkedin.com/jobs/view/123..."
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            data-testid="parser-url-input"
                            disabled={phase === "parsing"}
                        />
                    )}
                    {mode === "text" && (
                        <Textarea
                            rows={10}
                            placeholder="Paste the full job description here…"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            data-testid="parser-text-input"
                            disabled={phase === "parsing"}
                        />
                    )}
                    {mode === "file" && (
                        <div>
                            <input
                                ref={fileRef}
                                type="file"
                                accept=".pdf,.docx,.txt"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                data-testid="parser-file-input"
                                className="hidden"
                            />
                            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={phase === "parsing"}>
                                <Upload size={13} className="mr-2" /> {file ? file.name : "Choose file"}
                            </Button>
                        </div>
                    )}

                    <div className="mt-4 flex justify-end">
                        <Button onClick={parse} disabled={phase === "parsing"} data-testid="parser-run-btn">
                            {phase === "parsing" ? <Loader2 size={13} className="animate-spin mr-2" /> : <Sparkles size={13} className="mr-2" />}
                            {phase === "parsing" ? "Parsing…" : "Generate AI Hiring Brief™"}
                        </Button>
                    </div>
                    {phase === "parsing" && (
                        <div
                            data-testid="parser-progress-copy"
                            className="mt-3 flex items-center justify-end gap-2 text-xs text-muted-foreground"
                        >
                            <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-violet-500" />
                            </span>
                            <span key={progressStep} className="animate-in fade-in duration-300">
                                {PARSE_STEPS[progressStep]?.label || "Working…"}
                            </span>
                        </div>
                    )}
                    {phase === "error" && <div className="mt-3 text-xs text-destructive">{error}</div>}
                </div>
            )}

            {brief && <BriefView brief={brief} onReset={reset} />}

            <div className="card-soft p-4" data-testid="recruit-search-history">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-medium text-base">Recent searches</h3>
                    <span className="text-xs text-muted-foreground">{history.length}</span>
                </div>
                {history.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">No searches yet.</div>
                ) : (
                    <div className="divide-y divide-border">
                        {history.map((h) => (
                            <button
                                key={h.id}
                                onClick={() => setBrief(h)}
                                data-testid={`search-history-${h.id}`}
                                className="w-full py-3 flex items-center justify-between gap-3 text-left hover:bg-muted/30 rounded px-1 transition"
                            >
                                <div className="min-w-0">
                                    <div className="font-medium truncate">{h.brief?.job_title || h.source_ref}</div>
                                    <div className="text-xs text-muted-foreground truncate">
                                        {h.brief?.location || "—"} · {h.brief?.remote_policy || "—"} · {new Date(h.created_at).toLocaleDateString()}
                                    </div>
                                </div>
                                <ChevronRight size={14} className="text-muted-foreground" />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

const Chips = ({ items, tone = "muted" }) => {
    if (!items || items.length === 0) return <span className="text-xs text-muted-foreground italic">None detected</span>;
    return (
        <div className="flex flex-wrap gap-1.5">
            {items.map((s, i) => (
                <span key={i} className={`text-[11px] rounded-md px-2 py-0.5 ${tone === "violet" ? "bg-violet-500/10 text-violet-700 dark:text-violet-300" : "bg-muted"}`}>{s}</span>
            ))}
        </div>
    );
};

function BriefView({ brief, onReset }) {
    const navigate = useNavigate();
    const b = brief.brief || {};
    const hb = b.hiring_brief || {};
    const salary = b.salary || {};
    const salaryLabel = (salary.min && salary.max)
        ? `${salary.currency || "USD"} ${Number(salary.min).toLocaleString()} – ${Number(salary.max).toLocaleString()}`
        : (salary.raw || "Not specified");

    const copyKeywords = () => {
        const boolean = (b.keywords || []).map((k) => `"${k}"`).join(" OR ");
        navigator.clipboard.writeText(boolean);
        toast.success("Boolean search copied to clipboard.");
    };

    const archive = async () => {
        try {
            await api.delete(`/admin/recruit/searches/${brief.id}`);
            toast.success("Archived.");
            onReset();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div data-testid="recruit-brief-view" className="space-y-4">
            <div className="card-soft p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                        <Badge variant="outline" className="mb-2 border-violet-500/40 text-violet-700 dark:text-violet-400">AI Hiring Brief™</Badge>
                        <h2 className="font-heading font-medium text-2xl leading-tight">{b.job_title || "Untitled role"}</h2>
                        <div className="text-sm text-muted-foreground mt-1 flex flex-wrap gap-3">
                            {b.industry && <span>{b.industry}</span>}
                            {b.seniority && <span>· {b.seniority}</span>}
                            {b.location && <span>· <MapPin size={12} className="inline -mt-0.5" /> {b.location}</span>}
                            {b.remote_policy && <span>· {b.remote_policy}</span>}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={copyKeywords} data-testid="brief-copy-boolean">
                            <Copy size={12} className="mr-1" /> Copy Boolean
                        </Button>
                        <Button variant="ghost" size="sm" onClick={archive} data-testid="brief-archive"><Trash2 size={12} className="mr-1" /> Archive</Button>
                        <Button variant="ghost" size="sm" onClick={onReset} data-testid="brief-new-search">New search</Button>
                    </div>
                </div>

                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <BriefStat Icon={DollarSign} label="Salary" value={salaryLabel} />
                    <BriefStat Icon={GraduationCap} label="Education" value={(b.education || []).join(", ") || "—"} />
                    <BriefStat Icon={ShieldCheck} label="Certifications" value={(b.certifications || []).join(", ") || "—"} />
                    <BriefStat Icon={FileText} label="Experience" value={b.experience_level_years ? `${b.experience_level_years.min || 0}–${b.experience_level_years.max || 0} yrs` : "—"} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="card-soft p-4">
                    <h3 className="font-heading font-medium mb-2">Required skills</h3>
                    <Chips items={b.required_skills} tone="violet" />
                </div>
                <div className="card-soft p-4">
                    <h3 className="font-heading font-medium mb-2">Preferred skills</h3>
                    <Chips items={b.preferred_skills} />
                </div>
                <div className="card-soft p-4">
                    <h3 className="font-heading font-medium mb-2">Transferable skills</h3>
                    <Chips items={b.transferable_skills} />
                </div>
                <div className="card-soft p-4">
                    <h3 className="font-heading font-medium mb-2">Alternative titles</h3>
                    <Chips items={b.suggested_alternative_titles} />
                </div>
            </div>

            {(hb.must_haves?.length > 0 || hb.nice_to_haves?.length > 0) && (
                <div className="card-soft p-5">
                    <h3 className="font-heading font-medium mb-3">Hiring Brief™</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                            <div className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider mb-1">Must-haves</div>
                            <ul className="list-disc list-inside space-y-1">{(hb.must_haves || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
                        </div>
                        <div>
                            <div className="text-[11px] font-semibold text-sky-600 uppercase tracking-wider mb-1">Nice-to-haves</div>
                            <ul className="list-disc list-inside space-y-1">{(hb.nice_to_haves || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
                        </div>
                        {hb.day_in_the_life && (
                            <div className="md:col-span-2">
                                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">A day in the life</div>
                                <p className="text-muted-foreground">{hb.day_in_the_life}</p>
                            </div>
                        )}
                        {hb.red_flags_to_avoid?.length > 0 && (
                            <div className="md:col-span-2">
                                <div className="text-[11px] font-semibold text-rose-600 uppercase tracking-wider mb-1">Red flags to avoid</div>
                                <ul className="list-disc list-inside space-y-1">{hb.red_flags_to_avoid.map((x, i) => <li key={i}>{x}</li>)}</ul>
                            </div>
                        )}
                        {hb.screening_questions?.length > 0 && (
                            <div className="md:col-span-2">
                                <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Screening questions</div>
                                <ol className="list-decimal list-inside space-y-1">{hb.screening_questions.map((x, i) => <li key={i}>{x}</li>)}</ol>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="card-soft p-5 border-dashed">
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-violet-500/10 text-violet-600 flex items-center justify-center"><Send size={16} /></div>
                    <div className="flex-1">
                        <h3 className="font-heading font-medium">Next: search candidates</h3>
                        <p className="text-sm text-muted-foreground">
                            Match this brief against the Simonara Passport database first, then blend in Apollo results with AI ranking. See the Talent Rediscovery panel and get AI recommendations per candidate.
                        </p>
                    </div>
                    <Button
                        onClick={() => navigate(`/admin/recruit/candidate-search?search=${brief.id}`)}
                        data-testid="candidate-search-cta"
                    >
                        <Send size={13} className="mr-2" /> Search candidates
                    </Button>
                </div>
            </div>
        </div>
    );
}

const BriefStat = ({ Icon, label, value }) => (
    <div className="rounded-md bg-muted/40 p-3">
        <div className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
            <Icon size={11} /> {label}
        </div>
        <div className="text-sm mt-1 truncate" title={value}>{value}</div>
    </div>
);
